import React from "react";
import { Star, ClipboardPlus, Filter } from "lucide-react";
import { CryptoLogo, Sparkline } from "../common/CryptoBits";
import { fmtPrice, fmtPct, fmtCompact } from "../../lib/format";

export default function MarketOverview({ rows = [], loading, onSelect, onCreatePlan }) {
  return (
    <div className="ym-panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-bold">Ringkasan Pasar</h3>
        <div className="flex items-center gap-2">
          <span className="pill" style={{ color: "var(--text-muted)", background: "var(--panel-2)", borderColor: "var(--border)" }}>
            <Filter size={12} /> USD
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" style={{ minWidth: 820 }}>
          <thead>
            <tr style={{ color: "var(--text-dim)" }} className="text-left text-[10px] font-semibold tracking-wide">
              <th className="py-2 pl-1 font-semibold">PAIR</th>
              <th className="py-2 font-semibold text-right">PRICE</th>
              <th className="py-2 font-semibold text-right">1H</th>
              <th className="py-2 font-semibold text-right">24H</th>
              <th className="py-2 font-semibold text-right">VOLUME</th>
              <th className="py-2 font-semibold text-center">LONG</th>
              <th className="py-2 font-semibold text-center">SHORT</th>
              <th className="py-2 font-semibold text-center">REGIME</th>
              <th className="py-2 font-semibold text-center">PLAN</th>
              <th className="py-2 font-semibold text-center">CHART</th>
              <th className="py-2 pr-1 font-semibold text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              if (r.error) {
                return (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--border-soft)" }}>
                    <td className="py-2.5 pl-1"><div className="flex items-center gap-2"><CryptoLogo base={r.base} color={r.color} size={22} /><span className="font-bold">{r.base}/USDT</span></div></td>
                    <td colSpan={10} className="py-2.5 text-[11px]" style={{ color: "var(--red)" }}>Data unavailable from providers</td>
                  </tr>
                );
              }
              return (
                <tr key={r.id} className="ym-hover cursor-pointer" style={{ borderTop: "1px solid var(--border-soft)" }} onClick={() => onSelect(r.symbol)}>
                  <td className="py-2.5 pl-1">
                    <div className="flex items-center gap-2">
                      <CryptoLogo base={r.base} color={r.color} size={22} />
                      <div className="leading-tight">
                        <div className="font-bold">{r.base}<span style={{ color: "var(--text-dim)" }}>/USDT</span></div>
                        <div className="text-[9px]" style={{ color: "var(--text-dim)" }}>{r.provider}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 text-right tabnum font-semibold">${fmtPrice(r.price)}</td>
                  <td className="py-2.5 text-right tabnum" style={{ color: r.change1h >= 0 ? "var(--green)" : "var(--red)" }}>{fmtPct(r.change1h)}</td>
                  <td className="py-2.5 text-right tabnum" style={{ color: r.change24h >= 0 ? "var(--green)" : "var(--red)" }}>{fmtPct(r.change24h)}</td>
                  <td className="py-2.5 text-right tabnum" style={{ color: "var(--text-muted)" }}>{fmtCompact(r.volume24h)}</td>
                  <td className="py-2.5 text-center"><Badge v={r.longScore} color="var(--green)" /></td>
                  <td className="py-2.5 text-center"><Badge v={r.shortScore} color="var(--red)" /></td>
                  <td className="py-2.5 text-center"><span className="capitalize text-[11px]" style={{ color: "var(--accent-soft)" }}>{r.regime}</span></td>
                  <td className="py-2.5 text-center">
                    <span className="pill" style={{ fontSize: 9, padding: "1px 7px", color: r.direction === "LONG" ? "var(--green)" : r.direction === "SHORT" ? "var(--red)" : "var(--text-muted)", background: "var(--panel-2)", borderColor: "var(--border)" }}>{r.direction}</span>
                  </td>
                  <td className="py-2.5">
                    <div className="grid place-items-center"><Sparkline data={r.spark} color={r.change24h >= 0 ? "#22c55e" : "#ef4444"} width={80} height={26} /></div>
                  </td>
                  <td className="py-2.5 pr-1">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={(e) => { e.stopPropagation(); onSelect(r.symbol); }} className="grid place-items-center rounded-md ym-hover" style={{ width: 26, height: 26, background: "var(--panel-2)", border: "1px solid var(--border)" }} title="Favorit">
                        <Star size={12} style={{ color: "var(--amber)" }} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onCreatePlan(r.symbol); }} className="grid place-items-center rounded-md ym-hover" style={{ width: 26, height: 26, background: "rgba(139,92,246,.16)", border: "1px solid rgba(139,92,246,.3)" }} title="Buka Rencana">
                        <ClipboardPlus size={12} style={{ color: "var(--accent-soft)" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rows.length && loading && (
              <tr><td colSpan={11} className="py-8 text-center" style={{ color: "var(--text-dim)" }}>Loading live market data…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({ v, color }) {
  return <span className="tabnum font-bold text-[12px]" style={{ color }}>{v ?? "—"}</span>;
}
